import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { TOUR_VERSION, buildSteps } from './tourSteps';
import TourOverlay from './TourOverlay';

const TourContext = createContext({ start: () => {}, active: false });
export const useTour = () => useContext(TourContext);

const PUBLIC = ['/', '/login'];
const readUser = () => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } };

export function TourProvider({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [active, setActive] = useState(false);
  const [index, setIndex] = useState(0);
  const [steps, setSteps] = useState([]);
  const direction = useRef(1);
  const autoChecked = useRef(false);

  const start = useCallback(() => {
    setSteps(buildSteps(readUser()));
    direction.current = 1;
    setIndex(0);
    setActive(true);
  }, []);

  // Remember completion on the server so it follows the user across devices and tenant sites
  const finish = useCallback(async (status) => {
    setActive(false);
    const user = readUser();
    localStorage.setItem('user', JSON.stringify({ ...user, tour_version: TOUR_VERSION }));
    try {
      await axios.post('/api/users/me/tour', { version: TOUR_VERSION, status },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
    } catch { /* not critical */ }
  }, []);

  const next = useCallback(() => {
    direction.current = 1;
    setIndex((i) => (i + 1 >= steps.length ? i : i + 1));
    if (index + 1 >= steps.length) finish('completed');
  }, [steps.length, index, finish]);

  const back = useCallback(() => {
    direction.current = -1;
    setIndex((i) => Math.max(0, i - 1));
  }, []);

  // A step whose element isn't on the page (empty list, hidden button) is skipped in the direction of travel
  const skipMissing = useCallback(() => {
    if (direction.current < 0 && index === 0) return;
    direction.current < 0 ? back() : next();
  }, [back, next, index]);

  // First visit to the dashboard after logging in → start automatically
  useEffect(() => {
    if (autoChecked.current || active) return;
    if (location.pathname !== '/dashboard' || !localStorage.getItem('token')) return;
    autoChecked.current = true;
    const user = readUser();
    if ((user.tour_version || 0) < TOUR_VERSION) {
      const t = setTimeout(start, 900);
      return () => clearTimeout(t);
    }
  }, [location.pathname, active, start]);

  // Keep the page in step with the tour
  const step = active ? steps[index] : null;
  useEffect(() => {
    if (step && step.route && location.pathname !== step.route) navigate(step.route);
  }, [step, location.pathname, navigate]);

  // Leaving to a public page (logout) ends the tour
  useEffect(() => {
    if (active && PUBLIC.includes(location.pathname)) setActive(false);
  }, [active, location.pathname]);

  const value = useMemo(() => ({ start, active }), [start, active]);

  return (
    <TourContext.Provider value={value}>
      {children}
      {step && location.pathname === step.route && (
        <TourOverlay
          key={step.id}
          step={step}
          index={index}
          total={steps.length}
          onNext={next}
          onBack={back}
          onSkip={() => finish('skipped')}
          onMissing={skipMissing}
        />
      )}
    </TourContext.Provider>
  );
}
